import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { ClanNews, CreateClanNewsRequest, UpdateClanNewsRequest, NEWS_TYPES, MAX_CONTENT_LENGTH, MAX_TITLE_LENGTH, MAX_PRIORITY_NEWS } from '../../types/clanNews';
import clanNewsService from '../../services/clanNewsService';
import YouTubePlayer from '../common/YouTubePlayer';

interface ClanNewsFormProps {
  clanId: string;
  news?: ClanNews; // Для редактирования
  isOpen: boolean;
  onClose: () => void;
  onSave: (news: ClanNews) => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
`;

const ModalContainer = styled.div`
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  max-width: 700px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-sizing: border-box;
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 85vh;
    border-radius: 12px;
  }
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  color: #00d4ff;
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: rgba(244, 67, 54, 0.8);
  border: 2px solid #f44336;
  color: #ffffff;
  width: 35px;
  height: 35px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(244, 67, 54, 1);
  }
`;

const FormContent = styled.div`
  padding: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  color: #888;
  font-size: 0.9rem;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 8px;
  }
`;

const FormInput = styled.input`
  background: var(--legacy-card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid #00d4ff;
  border-radius: 8px;
  padding: 10px 15px;
  color: #e0e0e0;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
  }

  &::placeholder {
    color: #888;
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 1.1rem;
    border-radius: 10px;
  }
`;

const FormTextarea = styled.textarea`
  background: var(--legacy-card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid #00d4ff;
  border-radius: 8px;
  padding: 10px 15px;
  color: #e0e0e0;
  font-size: 1rem;
  line-height: 1.6;
  resize: vertical;
  min-height: 150px;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
  }

  &::placeholder {
    color: #888;
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 1.1rem;
    border-radius: 10px;
  }
`;

const FormSelect = styled.select`
  background: var(--legacy-card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid #00d4ff;
  border-radius: 8px;
  padding: 10px 15px;
  color: #e0e0e0;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
  }

  option {
    background: #1a1a1a;
    color: #e0e0e0;
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 1.1rem;
    border-radius: 10px;
  }
`;

const FormCheckbox = styled.input`
  margin-right: 10px;
  transform: scale(1.2);
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 1rem;
  color: #e0e0e0;
  cursor: pointer;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FileInput = styled.input`
  background: var(--legacy-card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid #00d4ff;
  border-radius: 8px;
  padding: 10px 15px;
  color: #e0e0e0;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 1.1rem;
    border-radius: 10px;
  }
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 200px;
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 8px;
  margin-top: 10px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  font-size: 1rem;
  backdrop-filter: blur(10px);
`;

const VideoPreview = styled.div`
  margin-top: 10px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #00d4ff;
`;

const CharacterCount = styled.div`
  font-size: 0.8rem;
  color: #888;
  text-align: right;
  margin-top: 5px;
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  font-size: 0.8rem;
  margin-top: 5px;
`;

const WarningMessage = styled.div`
  color: #ffa500;
  font-size: 0.8rem;
  margin-top: 5px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  padding: 20px;
  border-top: 1px solid rgba(0, 212, 255, 0.2);
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  padding: 12px 24px;
  background: ${props => props.primary ? '#00d4ff' : 'var(--legacy-card-bg)'};
  border: 2px solid ${props => props.primary ? '#00d4ff' : '#666'};
  color: ${props => props.primary ? '#000000' : '#e0e0e0'};
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover:not(:disabled) {
    background: ${props => props.primary ? '#ffffff' : 'rgba(0, 212, 255, 0.1)'};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 1.1rem;
    border-radius: 10px;
  }
`;

const ClanNewsForm: React.FC<ClanNewsFormProps> = ({ clanId, news, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: NEWS_TYPES.PUBLIC as 'Public' | 'Internal',
    isPriority: false,
    videoUrl: ''
  });
  
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [priorityNewsCount, setPriorityNewsCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      if (news) {
        // Редактирование существующей новости
        setFormData({
          title: news.title,
          content: news.content,
          type: news.type,
          isPriority: news.isPriority,
          videoUrl: news.videoUrl || ''
        });
        setImagePreview(news.imageUrl || '');
      } else {
        // Создание новой новости
        setFormData({
          title: '',
          content: '',
          type: NEWS_TYPES.PUBLIC,
          isPriority: false,
          videoUrl: ''
        });
        setImagePreview('');
      }
      setImageFile(null);
      setErrors({});
      loadPriorityNewsCount();
    }
  }, [isOpen, news]);

  const loadPriorityNewsCount = async () => {
    try {
      const count = await clanNewsService.getPriorityNewsCount(clanId);
      setPriorityNewsCount(count);
    } catch (error) {
      console.error('Error loading priority news count:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Очищаем ошибку при изменении поля
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB
        setErrors(prev => ({ ...prev, image: 'Image size must be less than 5MB' }));
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, image: 'Please select a valid image file' }));
        return;
      }
      
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, image: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > MAX_TITLE_LENGTH) {
      newErrors.title = `Title must be less than ${MAX_TITLE_LENGTH} characters`;
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    } else if (formData.content.length > MAX_CONTENT_LENGTH) {
      newErrors.content = `Content must be less than ${MAX_CONTENT_LENGTH} characters`;
    }
    
    if (formData.videoUrl && !clanNewsService.validateYouTubeUrl(formData.videoUrl)) {
      newErrors.videoUrl = 'Please enter a valid YouTube URL';
    }
    
    // Проверка лимита приоритетных новостей
    if (formData.isPriority && !news && priorityNewsCount >= MAX_PRIORITY_NEWS) {
      newErrors.isPriority = `Maximum ${MAX_PRIORITY_NEWS} priority news allowed`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      let result: ClanNews;
      
      if (news) {
        // Обновление существующей новости
        const updateData: UpdateClanNewsRequest = {
          title: formData.title,
          content: formData.content,
          type: formData.type,
          isPriority: formData.isPriority,
          videoUrl: formData.videoUrl || undefined
        };
        
        if (imageFile) {
          updateData.imageFile = imageFile;
        }
        
        result = await clanNewsService.updateClanNews(clanId, news.id, updateData);
      } else {
        // Создание новой новости
        const createData: CreateClanNewsRequest = {
          title: formData.title,
          content: formData.content,
          type: formData.type,
          isPriority: formData.isPriority,
          videoUrl: formData.videoUrl || undefined
        };
        
        if (imageFile) {
          createData.imageFile = imageFile;
        }
        
        result = await clanNewsService.createClanNews(clanId, createData);
      }
      
      onSave(result);
      onClose();
    } catch (error: any) {
      console.error('Error saving news:', error);
      setErrors({ submit: error.message || 'Failed to save news' });
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const canSetPriority = news ? true : priorityNewsCount < MAX_PRIORITY_NEWS;

  return createPortal(
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{news ? 'Edit News' : 'Create News'}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        
        <form onSubmit={handleSubmit}>
          <FormContent>
            <FormGroup>
              <FormLabel>Title *</FormLabel>
              <FormInput
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter news title..."
                maxLength={MAX_TITLE_LENGTH}
              />
              <CharacterCount>{formData.title.length}/{MAX_TITLE_LENGTH}</CharacterCount>
              {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <FormLabel>Content *</FormLabel>
              <FormTextarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Enter news content..."
                maxLength={MAX_CONTENT_LENGTH}
              />
              <CharacterCount>{formData.content.length}/{MAX_CONTENT_LENGTH}</CharacterCount>
              {errors.content && <ErrorMessage>{errors.content}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <FormLabel>Type *</FormLabel>
              <FormSelect
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value={NEWS_TYPES.PUBLIC}>Public (visible to everyone)</option>
                <option value={NEWS_TYPES.INTERNAL}>Internal (clan members only)</option>
              </FormSelect>
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <FormCheckbox
                  type="checkbox"
                  name="isPriority"
                  checked={formData.isPriority}
                  onChange={handleInputChange}
                  disabled={!canSetPriority}
                />
                Priority News (displayed as card)
              </CheckboxLabel>
              {!canSetPriority && (
                <WarningMessage>
                  Maximum {MAX_PRIORITY_NEWS} priority news allowed. Current: {priorityNewsCount}
                </WarningMessage>
              )}
              {errors.isPriority && <ErrorMessage>{errors.isPriority}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <FormLabel>Image (optional)</FormLabel>
              <FileInput
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {errors.image && <ErrorMessage>{errors.image}</ErrorMessage>}
              {imagePreview && (
                <ImagePreview style={{ backgroundImage: `url(${imagePreview})` }}>
                  {!imagePreview && 'NO IMAGE SELECTED'}
                </ImagePreview>
              )}
            </FormGroup>

            <FormGroup>
              <FormLabel>YouTube Video (optional)</FormLabel>
              <FormInput
                type="text"
                name="videoUrl"
                value={formData.videoUrl}
                onChange={handleInputChange}
                placeholder="https://youtu.be/VIDEO_ID or https://youtube.com/watch?v=VIDEO_ID"
              />
              {errors.videoUrl && <ErrorMessage>{errors.videoUrl}</ErrorMessage>}
              {formData.videoUrl && clanNewsService.validateYouTubeUrl(formData.videoUrl) && (
                <VideoPreview>
                  <YouTubePlayer 
                    videoUrl={formData.videoUrl} 
                    autoplay={false}
                  />
                </VideoPreview>
              )}
            </FormGroup>

            {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}
          </FormContent>
          
          <FormActions>
            <ActionButton type="button" onClick={onClose}>
              Cancel
            </ActionButton>
            <ActionButton type="submit" primary disabled={loading}>
              {loading ? 'Saving...' : (news ? 'Update' : 'Create')}
            </ActionButton>
          </FormActions>
        </form>
      </ModalContainer>
    </ModalOverlay>,
    document.body
  );
};

export default ClanNewsForm; 